// src/pages/ReportPage.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getAllReports, getReportStats, getReport } from "../api/axios";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";

export default function ReportPage() {
  const { submitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchPhone, setSearchPhone] = useState("");
  const [report, setReport] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 20;

  useEffect(() => {
  async function fetchData() {
    setLoading(true);
    try {
      if (submitId) {
        const { data: reportData } = await getReport(submitId);
        const { data: allReportsData } = await getAllReports();

        console.log("📦 [getReport]", reportData);
        console.log("📋 [getAllReports]", allReportsData);

        const matchingReport = allReportsData.find(
          (r) => r.id === parseInt(submitId)
        );

        if (!matchingReport) {
          setReport(null);
        } else {
          setReport({
            id: matchingReport.id,
            template_name: matchingReport.template_name,
            mode: matchingReport.mode,
            status: matchingReport.status,
            submitted_at: matchingReport.submitted_at,
            summary: reportData.summary,
            details: reportData.details,
          });
        }
      } else {
        // ✅ Simplified all reports loader
        const { data: reports } = await getAllReports();
        console.log("📋 [All Reports]", reports);
        setAllReports(reports);
      }
    } catch (err) {
      console.error("❌ Error fetching reports:", err.response?.data || err);
    }
    setLoading(false);
  }

  const interval = setInterval(fetchData, 60000);
  fetchData();
  return () => clearInterval(interval);
}, [submitId]);


  if (loading) return <Typography p={2}>Loading...</Typography>;

  if (!submitId) {
    if (allReports.length === 0)
      return <Typography p={2}>No reports found.</Typography>;

    const totalPages = Math.ceil(allReports.length / ITEMS_PER_PAGE);
    const paginatedReports = allReports.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      currentPage * ITEMS_PER_PAGE
    );

    return (
      <Box p={0}>
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 4px",
            backgroundColor: "#f9f9f9",
            borderRadius: "8px",
          }}
        >
          {/* <img src="/logo.png" alt="Logo" height={40} style={{ marginRight: 10 }} /> */}
          <Typography variant="h6" fontWeight="bold">
            All Reports
          </Typography>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
                <TableCell>ID</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Delivered</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>Failed</TableCell>
                <TableCell>Submitted At</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedReports.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.id}</TableCell>
                  <TableCell>{row.template_name}</TableCell>
                  <TableCell>{row.mode}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.total_recipients}</TableCell>
                  <TableCell>{row.success_count}</TableCell>
                  <TableCell>{row.delivered_count}</TableCell>
                  <TableCell>{row.read_count}</TableCell>
                  <TableCell>{row.failed_count}</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    {new Date(row.submitted_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        color="primary"
                        component={Link}
                        to={`/reports/${row.id}`}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack mt={2} alignItems="center">
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(e, value) => setCurrentPage(value)}
            color="primary"
          />
        </Stack>
      </Box>
    );
  }

  if (!report) return <Typography p={2}>No report data found.</Typography>;

  const { summary, details, template_name, id, mode, status, submitted_at } =
    report;

  const filteredDetails = details.filter(
    (d) =>
      (statusFilter === "All" || d.status === statusFilter) &&
      (searchPhone.trim() === "" || d.phone.includes(searchPhone.trim()))
  );

  const totalPages = Math.ceil(filteredDetails.length / ITEMS_PER_PAGE);
  const paginatedDetails = filteredDetails.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <Box p={2}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/reports")}
        sx={{ mb: 2 }}
      >
        Back to All Reports
      </Button>

      <Typography variant="h6" fontWeight="bold" mb={1}>
        {template_name} (ID: {id})
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
              <TableCell>Mode</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Sent</TableCell>
              <TableCell>Delivered</TableCell>
              <TableCell>Read</TableCell>
              <TableCell>Failed</TableCell>
              <TableCell>Submitted At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{mode}</TableCell>
              <TableCell>{status}</TableCell>
              <TableCell>{summary?.total}</TableCell>
              <TableCell>{summary?.sent}</TableCell>
              <TableCell>{summary?.delivered ?? "-"}</TableCell>
              <TableCell>{summary?.read ?? "-"}</TableCell>
              <TableCell>{summary?.failed}</TableCell>
              <TableCell>
                {submitted_at ? new Date(submitted_at).toLocaleString() : "-"}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Sent">Sent</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Delivered">Delivered</MenuItem>
            <MenuItem value="Read">Read</MenuItem>
            <MenuItem value="Failed">Failed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Search Phone"
          size="small"
          value={searchPhone}
          onChange={(e) => {
            setSearchPhone(e.target.value);
            setCurrentPage(1);
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5", fontWeight: "bold" }}>
              <TableCell>#</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Sent At</TableCell>
              <TableCell>Delivered At</TableCell>
              <TableCell>Read At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedDetails.map((row, idx) => (
              <TableRow key={row.phone} hover>
                <TableCell>
                  {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                </TableCell>
                <TableCell>{row.phone}</TableCell>
                <TableCell
                  sx={{
                    color: statusColor(row.status),
                    fontWeight: "bold",
                  }}
                >
                  {row.status}
                </TableCell>
                <TableCell>{row.sent_at || "-"}</TableCell>
                <TableCell>{row.delivered_at || "-"}</TableCell>
                <TableCell>{row.read_at || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack mt={2} alignItems="center">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(e, value) => setCurrentPage(value)}
          color="primary"
        />
      </Stack>
    </Box>
  );
}

function statusColor(status) {
  if (status === "Sent") return "blue";
  if (status === "Pending") return "orange";
  if (status === "Delivered") return "green";
  if (status === "Read") return "purple";
  if (status === "Failed") return "red";
  return "#000";
}
